import isEqual from 'lodash/isEqual';
import { createSelector } from '@reduxjs/toolkit';
import { DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN, ENABLED_FOR_TEST_RIDE_ATTR, MOTORCYCLE_LOWERED_VERSION_KEY, MOTORCYCLE_POWER_VERSION_KEY, MOTORCYCLE_TRANSMISSION_KEY, } from '@yme/selectors/constants';
import { commonSelector, PRODUCT_LONG_INTRO_B, PRODUCT_LONG_INTRO_C, PRODUCT_OVERVIEW_HEADER, } from '@yme/selectors/index';
import { getValueOptionByLang } from '@yme/utils/common';
import { getCommon, getState } from './common';
import { PRODUCT_BLOCK_STRATEGY } from './constants';
import { buildFullListImages, getAllVariantsForProduct, getAttributeByName, getFullPowerYAMTVariant, getGroupingDataVariants, hasGroupingProducts, selectProductVariantsByColor, } from './helpers';
export const getProductDetailsData = (state) => state.productDetailsPage?.data;
export const getMotorcycleVariationsStatus = createSelector(getProductDetailsData, (data) => {
    return hasGroupingProducts(data);
});
export const getProductFullPowerYAMTSku = createSelector([getProductDetailsData, getMotorcycleVariationsStatus], (data, isGroupingProduct) => {
    return getFullPowerYAMTVariant({
        isGroupingProduct,
        data,
    });
});
export const getProductDetailsGroupingData = (state) => {
    return getGroupingDataVariants(state.productDetailsPage?.data?.groupingProducts ?? []);
};
export const getProductDetailsVariants = (state) => state.productDetailsPage?.data?.variants;
const buildProductDetailsVariantsSKUMap = (variants) => {
    const masterSku = variants.find((product) => product.master)?.sku;
    if (!masterSku) {
        return {};
    }
    return variants.reduce((acc, product) => {
        acc[product.sku] = {
            masterSku,
            isTestRideEnabled: product?.attributes.find(getAttributeByName(ENABLED_FOR_TEST_RIDE_ATTR))?.value || false,
        };
        return acc;
    }, {});
};
export const getProductDetailsVariantsSKUMap = createSelector([getProductDetailsData], (data) => {
    if (!data) {
        return {};
    }
    const masterProductSkuesList = buildProductDetailsVariantsSKUMap(data.variants);
    if (!data?.groupingProducts?.length) {
        return masterProductSkuesList;
    }
    const variationsProductSkuesList = data?.groupingProducts?.reduce((acc, group) => {
        const groupResult = buildProductDetailsVariantsSKUMap(group.variants);
        Object.assign(acc, groupResult);
        return acc;
    }, {});
    return {
        ...masterProductSkuesList,
        ...variationsProductSkuesList,
    };
});
export const getProductDetailsVariantsWithGroupingProducts = createSelector([getProductDetailsVariants, getProductDetailsGroupingData], (variants, groupingVariants) => {
    return getAllVariantsForProduct(variants, groupingVariants);
}, {
    memoizeOptions: {
        resultEqualityCheck: isEqual,
    },
});
export const getProductYAMTStatus = createSelector(getProductDetailsVariantsWithGroupingProducts, (variants) => {
    return variants.some((variant) => {
        const transmission = variant?.attributes.find(getAttributeByName(MOTORCYCLE_TRANSMISSION_KEY))?.value;
        return transmission?.en === DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.yAMTTransmission;
    });
});
const getLocaleNameByAttributeKey = (attributeKey, defaultValueEn) => createSelector([getProductDetailsVariantsWithGroupingProducts, getCommon], (variantsData, common) => {
    const getAttributeFromVariant = (attributes) => {
        return attributes?.find(getAttributeByName(attributeKey))?.value;
    };
    const targetVariant = variantsData.find((variant) => {
        const attributeValue = getAttributeFromVariant(variant?.attributes);
        return attributeValue?.en === defaultValueEn;
    });
    const attribute = getAttributeFromVariant(targetVariant?.attributes);
    return getValueOptionByLang(attribute, common.language) || '';
});
export const getProduct35KWPowerLocaleName = getLocaleNameByAttributeKey(MOTORCYCLE_POWER_VERSION_KEY, DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.power35kw);
export const getProductStandardTransmissionLocaleName = getLocaleNameByAttributeKey(MOTORCYCLE_TRANSMISSION_KEY, DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.standardTransmission);
export const getProductLoweredVersionLocaleName = getLocaleNameByAttributeKey(MOTORCYCLE_LOWERED_VERSION_KEY, DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.loweredVersion);
export const getProductDetailsVariantsImages = createSelector(getProductDetailsVariants, (variants) => {
    let concat = [];
    variants?.forEach((item) => {
        concat = [...concat, ...item.images];
    });
    return concat;
});
export const getVariantsLongIntroductionText = createSelector(getProductDetailsVariants, (variants) => {
    let concat = [];
    variants?.forEach((item) => {
        const data = {
            sku: item.sku,
            productOverviewHeader: item?.attributes.find((attribute) => attribute.name === PRODUCT_OVERVIEW_HEADER)?.value,
            productLongIntroB: item?.attributes.find((attribute) => attribute.name === PRODUCT_LONG_INTRO_B)?.value,
            productLongIntroC: item?.attributes.find((attribute) => attribute.name === PRODUCT_LONG_INTRO_C)?.value,
        };
        concat = [
            ...concat,
            {
                ...data,
            },
        ];
    });
    return concat;
});
export const getFullImagesList = createSelector([getProductDetailsVariantsWithGroupingProducts], (variantsData) => (variantsData && buildFullListImages(variantsData)) || []);
export const getProductVariantsByColor = createSelector([getProductDetailsVariants, getCommon], (variantsData, common) => variantsData &&
    common?.language &&
    selectProductVariantsByColor({
        variantsData,
        language: common.language,
    }));
export const getProductVariantsByColorWithGroupingProducts = createSelector([getProductDetailsVariantsWithGroupingProducts, getCommon, getProduct35KWPowerLocaleName], (variantsData, common, power35KWLocaleName) => variantsData &&
    common?.language &&
    selectProductVariantsByColor({
        variantsData,
        language: common.language,
        power35KWLocaleName,
    }));
export const getMasterData = createSelector(getProductDetailsVariants, (variants) => variants?.find((item) => item.master));
export const getMasterDataSku = createSelector(getMasterData, (masterData) => masterData?.sku ?? '');
export function getAttribute(attributeName) {
    return createSelector(getMasterData, (masterData) => masterData?.attributes.find((attribute) => attribute.name === attributeName)?.value);
}
export const getAttributes = createSelector(getMasterData, (masterDataState) => masterDataState?.attributes || []);
export const getProductBlockStrategy = createSelector(getMasterData, (masterData) => masterData?.attributes.find((attribute) => attribute.name === PRODUCT_BLOCK_STRATEGY));
export const getProductDetails = createSelector(getState, (state) => state.productDetailsPage);
export const getIsLoadedProductDetails = createSelector(getProductDetails, (state) => state.isLoaded);
export const getProductCarouselParams = createSelector([
    commonSelector.getRTL,
    commonSelector.getRootLang,
    commonSelector.getIsInAuthor,
    commonSelector.getPDPLinks,
    commonSelector.getCommonLabels,
], (isRightToLeft, language, isInAuthor, pdpLinks, labels) => ({
    isRightToLeft,
    rootLanguage: language,
    commonLabels: labels,
    isInAuthor,
    pdpLinks,
}));
export const getProductSlug = createSelector(getProductDetails, (state) => state.data?.slug ?? '');
export const getProductName = createSelector(getProductDetails, (state) => state.data?.name ?? '');
